import { Quote } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

const testimonials = [
  {
    quote: "Their layer feed has boosted our egg production by nearly 18%. The technical support team visits regularly and adjusts rations based on our flock's actual performance data.",
    name: "Gurpreet Kaur",
    location: "Ludhiana, Punjab",
    farm: "★★★★★"
  },
  {
    quote: "We switched to Sandhu Feeds Finisher three cycles ago. Our broilers consistently hit 2kg body weight with a 34% boneless ratio — our processing plant buyers noticed the difference immediately.",
    name: "Mohammad Iqbal",
    location: "Panipat, Haryana",
    farm: "★★★★★"
  },
  {
    quote: "The Concentrated Feed saves us storage space and the mixing flexibility is unmatched. Sandhu Brothers understand what small-scale farmers actually need — practical solutions, not just products.",
    name: "Harjeet Dhillon",
    location: "Bathinda, Punjab",
    farm: "★★★★☆"
  },
  {
    quote: "I've been sourcing from Sandhu Feeds since they started. The consistency batch after batch is what keeps me loyal — my mortality rates dropped and growth uniformity improved across the shed.",
    name: "Balwinder Kaur",
    location: "Ambala, Haryana",
    farm: "★★★★★"
  },
  {
    quote: "Their Starter feed ensures our chicks transition smoothly from pre-starter. The amino acid profile is well-balanced — we see strong feathering and gut health in the second week itself.",
    name: "Sukhdev Sharma",
    location: "Patiala, Punjab",
    farm: "★★★★★"
  },
  {
    quote: "Sandhu Feeds delivers on time, every time. In poultry, even a one-day delay in feed supply can hurt performance. Their reliability is as important to us as the feed quality itself.",
    name: "Amandeep Gill",
    location: "Sangrur, Punjab",
    farm: "★★★★★"
  },
  {
    quote: "The FCR we are getting with Sandhu's Broiler Starter Crumb is unbeatable in the Meerut market. Our birds reached target weight 2 days earlier than the last batch. Truly a premium product.",
    name: "Sanjeev Tyagi",
    location: "Meerut, Uttar Pradesh",
    farm: "★★★★★"
  },
  {
    quote: "Switched to Sandhu Feeds for their Broiler Concentrates. The birds are much more uniform in size, and we've seen a significant drop in mortality during the first week.",
    name: "Mohit Sharma",
    location: "Agra, Uttar Pradesh",
    farm: "★★★★★"
  },
  {
    quote: "Excellent quality finisher pellets. No powdering issues, which means zero wastage in the feeders. Best poultry feed supplier for bulk orders in UP.",
    name: "Rajesh Yadav",
    location: "Varanasi, Uttar Pradesh",
    farm: "★★★★☆"
  },
  {
    quote: "Managing broiler health in the Jodhpur heat is tough, but Sandhu's Pre-Starter gives them the strong immunity they need right from day one. Very satisfied with the protein levels.",
    name: "Vikram Singh",
    location: "Jodhpur, Rajasthan",
    farm: "★★★★★"
  },
  {
    quote: "The results speak for themselves. Our birds are healthy, active, and the meat quality is top-notch. Sandhu Feeds is now our permanent partner for all our poultry sheds.",
    name: "Anil Choudhary",
    location: "Udaipur, Rajasthan",
    farm: "★★★★★"
  },
  {
    quote: "Reliable supply and consistent quality. Even in the winter months, the energy levels in their broiler feed kept our flock thriving. Highly recommend for Jammu farmers.",
    name: "Gurfateh Singh",
    location: "Jammu, Jammu and Kashmir",
    farm: "★★★★★"
  },
  {
    quote: "We tried several brands, but the growth rate with Sandhu's Starter Crumb is the most consistent. The packaging is excellent, ensuring the feed stays fresh during transport.",
    name: "Imtiyaz Ahmed",
    location: "Srinagar, Jammu and Kashmir",
    farm: "★★★★☆"
  },
  {
    quote: "Best Broiler Finisher in the MP region. The weight gain is steady and the birds have great feathering. It's clear they use high-quality ingredients without unnecessary fillers.",
    name: "Deepak Patidar",
    location: "Indore, Madhya Pradesh",
    farm: "★★★★★"
  },
  {
    quote: "I've been in the poultry business for a decade, and the technical support and feed quality from Sandhu Feeds are unmatched. Our profit margins have improved thanks to the better FCR.",
    name: "Suresh Meena",
    location: "Bhopal, Madhya Pradesh",
    farm: "★★★★★"
  },
  {
    quote: "Prompt delivery to Jabalpur and the feed is always fresh. The chicks take to the Pre-Starter immediately. Great experience dealing with the Sandhu team from Karnal.",
    name: "Amit G.",
    location: "Jabalpur, Madhya Pradesh",
    farm: "★★★★☆"
  },
];

const TestimonialsSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    onSelect();
    api.on("select", onSelect);
    return () => { api.off("select", onSelect); };
  }, [api, onSelect]);

  // Auto-play with pause on hover
  useEffect(() => {
    if (!api) return;
    let interval: ReturnType<typeof setInterval>;
    const play = () => { interval = setInterval(() => api.scrollNext(), 5000); };
    const stop = () => clearInterval(interval);

    const root = api.rootNode();
    play();
    root.addEventListener("mouseenter", stop);
    root.addEventListener("mouseleave", play);
    return () => {
      stop();
      root.removeEventListener("mouseenter", stop);
      root.removeEventListener("mouseleave", play);
    };
  }, [api]);

  return (
    <section className="py-32 bg-secondary/30 relative overflow-hidden">
      <div className="absolute left-8 top-12 text-[18rem] font-display text-foreground/[0.02] leading-none select-none pointer-events-none">
        "
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-20">
          <span className="text-accent font-body text-xs uppercase tracking-[0.25em] font-semibold">Testimonials</span>
          <h2 className="text-5xl md:text-6xl font-bold text-foreground font-display mt-3 leading-[1.05]">
            Trusted by
            <br />
            <span className="italic font-normal">farmers</span>
          </h2>
        </div>

        <Carousel setApi={setApi} opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-4">
            {testimonials.map((t, index) => (
              <CarouselItem key={index} className="pl-4 md:basis-1/3 basis-full">
                <div className="bg-card rounded-2xl p-8 md:p-10 border border-border/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col">
                  <Quote className="h-8 w-8 text-accent/30 mb-6 shrink-0" />
                  <p className="text-foreground/80 font-body leading-relaxed mb-8 text-[15px] flex-1">
                    {t.quote}
                  </p>
                  <div className="border-t border-border pt-6">
                    <p className="font-bold text-foreground font-display text-lg">{t.name}</p>
                    <p className="text-sm text-muted-foreground font-body">{t.location}</p>
                    <p className="text-xs text-accent font-body font-semibold uppercase tracking-wider mt-1">{t.farm}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="hidden md:block">
            <CarouselPrevious className="-left-4 lg:-left-12" />
            <CarouselNext className="-right-4 lg:-right-12" />
          </div>
        </Carousel>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === current ? "w-8 bg-accent" : "w-2 bg-border"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
